import { formatDistanceToNow } from "date-fns";
import { Sparkles, ShieldCheck, Activity, AlertTriangle, Trophy, Compass, MessageCircle } from "lucide-react";
import { useParentEngineSummary, type EngineLearningEvent } from "@/hooks/useParentEngineSummary";

interface Props {
  childId: string;
  childName: string;
}

const STATUS_META: Record<string, { label: string; tone: string }> = {
  active: { label: "Actively learning", tone: "text-emerald-300 border-emerald-400/30 bg-emerald-500/10" },
  in_progress: { label: "In progress", tone: "text-cyan-300 border-cyan-400/30 bg-cyan-500/10" },
  ready_to_advance: { label: "Ready to advance", tone: "text-emerald-300 border-emerald-400/30 bg-emerald-500/10" },
  needs_review: { label: "Needs a quick review", tone: "text-amber-300 border-amber-400/30 bg-amber-500/10" },
  inactive: { label: "Inactive lately", tone: "text-muted-foreground border-white/10 bg-white/5" },
  not_started: { label: "Not started yet", tone: "text-muted-foreground border-white/10 bg-white/5" },
};

const ACTION_LABELS: Record<string, string> = {
  start_first_module: "Begin the first module",
  start_lesson: "Start the next lesson",
  resume_lesson: "Resume the current lesson",
  retry_lesson_quiz: "Retry the recent quiz",
  retake_lesson_required: "Retake the lesson",
  continue_to_next_lesson: "Continue to the next lesson",
  module_complete: "Module complete — pick the next one",
  retake_module_required: "Retake the module",
  dashboard_review: "Review what they've learned",
  locked: "Waiting to unlock",
  error_safe_locked: "Paused for a safety check",
};

const EVENT_LABELS: Record<string, string> = {
  lesson_started: "Started a lesson",
  lesson_completed: "Completed a lesson",
  quiz_passed: "Passed a quiz",
  quiz_failed: "Missed a quiz — kept trying",
  module_retake_started: "Restarted a module for a refresher",
  lumi_holocard_opened: "Opened Lumi for extra help",
  lumi_support_used: "Used Lumi support during a lesson",
  voice_moment_played: "Played a spoken moment",
  dashboard_returned: "Returned to their dashboard",
};

function formatEvent(e: EngineLearningEvent): string {
  return EVENT_LABELS[e.event_type] ?? e.event_type.replace(/_/g, " ");
}

/**
 * Engine-backed Parent Hub panel. Reads exclusively from the
 * `get_parent_hub_learning_summary` RPC (learning_engine_decisions,
 * learning_event_ledger, learner_mastery, quiz_attempts, module_completions,
 * user_progress). Translates engine audit codes into parent-friendly copy.
 */
export default function EngineSignalsPanel({ childId, childName }: Props) {
  const { data, loading, error } = useParentEngineSummary(childId);
  const first = childName.split(" ")[0] || childName;

  if (loading) {
    return <div className="h-64 animate-pulse rounded-3xl border border-white/[0.06] bg-card/40 backdrop-blur-2xl" />;
  }
  if (error || !data) {
    return (
      <div className="rounded-3xl border border-white/[0.06] bg-card/40 p-5 backdrop-blur-2xl">
        <p className="text-sm text-muted-foreground">Engine signals are unavailable right now.</p>
      </div>
    );
  }

  const statusMeta = STATUS_META[data.status_label] ?? { label: data.status_label, tone: "text-muted-foreground border-white/10 bg-white/5" };
  const nextAction = data.current_next_action;
  const actionLabel = nextAction ? ACTION_LABELS[nextAction.action] ?? nextAction.action.replace(/_/g, " ") : null;
  const lastActive = data.last_activity_at ? formatDistanceToNow(new Date(data.last_activity_at), { addSuffix: true }) : "no activity yet";
  const support = data.support_usage_30d ?? {};

  return (
    <section className="relative overflow-hidden rounded-[24px] border border-white/[0.07] ring-1 ring-inset ring-white/[0.04] bg-[linear-gradient(180deg,hsl(var(--card)/0.78)_0%,hsl(var(--card)/0.5)_100%)] p-5 sm:p-6 backdrop-blur-2xl shadow-[0_30px_72px_-40px_rgba(0,0,0,0.7)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-primary/90">HeirWise Learning Engine</p>
          <h2 className="mt-0.5 font-heading text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
            Engine signals for {first}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">Live read of {first}'s progress, mastery, and support activity.</p>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium ${statusMeta.tone}`}>
          <Activity className="h-3 w-3" />
          {statusMeta.label}
        </span>
      </div>

      {/* Current status + next action */}
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-primary/20 bg-primary/[0.06] p-4">
          <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.18em] text-primary/90">
            <Compass className="h-3 w-3" /> Next best step
          </div>
          {nextAction ? (
            <>
              <p className="mt-1.5 text-sm font-medium text-foreground">{actionLabel}</p>
              {nextAction.reason && (
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{nextAction.reason}</p>
              )}
              {nextAction.is_locked && nextAction.lock_reason && (
                <p className="mt-2 inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-200">
                  <AlertTriangle className="h-3 w-3" /> Locked: {nextAction.lock_reason}
                </p>
              )}
            </>
          ) : (
            <p className="mt-1.5 text-sm text-muted-foreground">The engine will surface a next step after {first}'s first learning moment.</p>
          )}
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-background/30 p-4">
          <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            <Sparkles className="h-3 w-3" /> Progress
          </div>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <div>
              <p className="text-2xl font-semibold text-foreground">{data.completed_lessons_count}</p>
              <p className="text-[11px] text-muted-foreground">Lessons completed</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground">{data.completed_modules_count}</p>
              <p className="text-[11px] text-muted-foreground">Modules completed</p>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">Last active {lastActive}</p>
        </div>
      </div>

      {/* Mastery */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <MasteryList
          title="Strongest areas"
          icon={<Trophy className="h-3 w-3" />}
          tone="text-emerald-300"
          items={data.mastery_strengths}
          emptyText={`Mastery data will appear as ${first} answers more questions.`}
        />
        <MasteryList
          title="May need focus"
          icon={<AlertTriangle className="h-3 w-3" />}
          tone="text-amber-300"
          items={data.mastery_needs_focus}
          emptyText={`Nothing flagged. ${first} is holding steady across recent concepts.`}
        />
      </div>

      {/* Recent quiz activity */}
      <div className="mt-4 rounded-2xl border border-white/[0.06] bg-background/30 p-4">
        <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Recent quiz activity</p>
        {data.recent_quiz_attempts.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No quizzes attempted yet.</p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {data.recent_quiz_attempts.map((q, i) => (
              <li key={i} className="flex items-center justify-between gap-3 text-xs">
                <span className="flex items-center gap-2 text-foreground/90">
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${q.passed ? "bg-emerald-400" : "bg-amber-400"}`} />
                  {q.passed ? "Passed" : "Missed"} · Attempt {q.attempt_number ?? "?"}
                  {typeof q.score_percent === "number" && (
                    <span className="text-muted-foreground">· {Math.round(q.score_percent)}%</span>
                  )}
                </span>
                <span className="text-muted-foreground">{formatDistanceToNow(new Date(q.created_at), { addSuffix: true })}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Support usage + recent events */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-white/[0.06] bg-background/30 p-4">
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Support used (last 30 days)</p>
          <ul className="mt-2 space-y-1 text-xs">
            <SupportRow label="Opened Lumi holocard" value={support.lumi_holocard_opened} />
            <SupportRow label="Used Lumi support" value={support.lumi_support_used} />
            <SupportRow label="Played spoken moments" value={support.voice_moment_played} />
            <SupportRow label="Retried modules" value={support.module_retake_started} />
          </ul>
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-background/30 p-4">
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Recent learning activity</p>
          {data.recent_learning_events.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Nothing to show yet.</p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {data.recent_learning_events.slice(0, 6).map((e, i) => (
                <li key={i} className="flex items-center justify-between gap-3 text-xs">
                  <span className="text-foreground/90">{formatEvent(e)}</span>
                  <span className="text-muted-foreground">{formatDistanceToNow(new Date(e.created_at), { addSuffix: true })}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Parent recommendation */}
      <div className="mt-4 rounded-2xl border border-secondary/20 bg-gradient-to-br from-secondary/[0.08] to-primary/[0.05] p-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-2xl bg-secondary/15 ring-1 ring-secondary/20">
            <MessageCircle className="h-4 w-4 text-secondary-foreground/80" />
          </span>
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">How you can help</p>
            <p className="mt-1 text-sm leading-relaxed text-foreground/90">{data.parent_recommendation}</p>
          </div>
        </div>
      </div>

      <p className="mt-4 flex items-center gap-1.5 text-[10px] text-muted-foreground/80">
        <ShieldCheck className="h-3 w-3" /> Sourced from the HeirWise Adaptive Learning Engine. Access is restricted to verified household parents.
      </p>
    </section>
  );
}

function MasteryList({ title, icon, tone, items, emptyText }: { title: string; icon: React.ReactNode; tone: string; items: { label: string | null; score: number }[]; emptyText: string }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-background/30 p-4">
      <div className={`flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.18em] ${tone}`}>{icon}{title}</div>
      {items.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {items.map((m, i) => (
            <li key={i} className="flex items-center justify-between gap-3 text-xs">
              <span className="truncate text-foreground/90">{m.label ?? "General"}</span>
              <span className="text-muted-foreground">{Math.round(m.score)}%</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SupportRow({ label, value }: { label: string; value: number | undefined }) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-foreground/90">{label}</span>
      <span className="text-muted-foreground">{value ?? 0}</span>
    </li>
  );
}